import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Unplug, Send, BellRing } from "lucide-react";
import { api, errorMessage } from "../api";
import { fmtDT } from "../lib/units";

function SmsPanel() {
  const [c, setC] = useState(null);
  const [f, setF] = useState({ authkey: "", sender_id: "", flows: {} });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  const apply = (d) => { setC(d); setF({ authkey: "", sender_id: d.sender_id, flows: d.flows || {} }); };
  useEffect(() => { api.get("/sms/connection").then(({ data }) => apply(data)).catch((e) => setErr(errorMessage(e))); }, []);
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setMsg("");
    try { apply((await api.put("/sms/connection", { ...f, app_origin: window.location.origin })).data); setMsg("MSG91 verified and saved"); }
    catch (x) { setErr(errorMessage(x)); } finally { setBusy(false); }
  };
  const disconnect = async () => { if (!window.confirm("Remove your MSG91 SMS account?")) return; apply((await api.delete("/sms/connection")).data); };
  if (!c) return <div className="panel">{err || "Loading…"}</div>;
  return (
    <div className="panel" data-testid="sms-panel">
      <div className="panel-head"><div><span className="overline">MSG91 · SMS BACKUP</span><h2>Your SMS account</h2></div>
        <span className={`pill ${c.connected ? "success" : "mod-note"}`} data-testid="sms-mode">{c.connected ? "LIVE" : "NOT CONNECTED"}</span></div>
      <p className="form-hint">Used when a WhatsApp message can't be delivered. Texts go from your own DLT-approved sender ID. In MSG91, create one Flow per message and paste its template ID below. Use the variable names exactly as shown.</p>
      <form onSubmit={save} className="wa-form" data-testid="sms-form">
        <label>MSG91 auth key{c.connected && ` (saved ${c.key_hint} — leave blank to keep)`}<input type="password" autoComplete="off" value={f.authkey} onChange={(e) => setF({ ...f, authkey: e.target.value.trim() })} data-testid="sms-authkey" /></label>
        <label>DLT sender ID (6 letters)<input maxLength={6} value={f.sender_id} onChange={(e) => setF({ ...f, sender_id: e.target.value.toUpperCase() })} data-testid="sms-sender-id" /></label>
        {Object.entries(c.kinds).map(([k, v]) => (
          <label key={k}>{v.label} — template ID <small className="muted-label">vars: {v.vars.map((x) => `##${x}##`).join(" ")}</small>
            <input value={f.flows[k] || ""} onChange={(e) => setF({ ...f, flows: { ...f.flows, [k]: e.target.value.trim() } })} data-testid={`sms-flow-${k}`} /></label>
        ))}
        {err && <div className="auth-alert error" data-testid="sms-error">{err}</div>}
        {msg && <div className="auth-alert info" data-testid="sms-message">{msg}</div>}
        <div style={{ display: "flex", gap: 6 }}>
          <button className="primary-button" disabled={busy} data-testid="sms-save">{busy ? <Loader2 className="spin" size={14} /> : <ShieldCheck size={14} />} Verify &amp; save</button>
          {c.connected && <button type="button" className="secondary-button" onClick={disconnect} data-testid="sms-disconnect"><Unplug size={13} /> Disconnect</button>}
        </div>
      </form>
    </div>
  );
}

const TOGGLES = [["food_ready", "Food ready → captain", "Pops up on the captain's phone; also sent on WhatsApp/SMS if they're not in the app"],
  ["low_stock", "Low stock → owner", "Sent the moment an ingredient drops to its reorder level"],
  ["bill_auto", "Bill → guest automatically", "Every bill with a guest mobile number is sent from your number"]];

function AlertsPanel() {
  const [s, setS] = useState(null);
  const [log, setLog] = useState([]);
  const [phone, setPhone] = useState("");
  const [msg, setMsg] = useState("");
  const loadLog = () => api.get("/notifications").then(({ data }) => setLog(data)).catch(() => {});
  useEffect(() => { api.get("/alerts/settings").then(({ data }) => setS(data)).catch((e) => setMsg(errorMessage(e))); loadLog(); }, []);
  const toggle = async (k) => { try { setS((await api.put("/alerts/settings", { ...s, [k]: !s[k] })).data); } catch (e) { setMsg(errorMessage(e)); } };
  const test = async (kind) => {
    setMsg("");
    try { const { data } = await api.post("/alerts/test", { kind, phone }); setMsg(`${data.status}${data.channel ? ` via ${data.channel}` : ""}${data.error ? ` — ${data.error}` : ""}`); loadLog(); }
    catch (e) { setMsg(errorMessage(e)); }
  };
  if (!s) return <div className="panel">{msg || "Loading…"}</div>;
  return (
    <div className="panel" data-testid="alerts-panel">
      <div className="panel-head"><div><span className="overline">REAL ALERTS</span><h2><BellRing size={16} /> What gets sent</h2></div></div>
      <p className="form-hint">The daily sales summary follows your digest settings (time and recipients). Staff login codes are always sent.</p>
      {TOGGLES.map(([k, label, hint]) => (
        <label key={k} className="al-toggle"><input type="checkbox" checked={!!s[k]} onChange={() => toggle(k)} data-testid={`alert-toggle-${k}`} /><span><b>{label}</b><small>{hint}</small></span></label>
      ))}
      <div className="wa-test">
        <input placeholder="10-digit mobile for a test" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="alert-test-phone" />
        {["food_ready", "low_stock", "bill"].map((k) => <button key={k} className="secondary-button" disabled={!phone} onClick={() => test(k)} data-testid={`alert-test-${k}`}><Send size={12} /> Test {k.replace("_", " ")}</button>)}
      </div>
      {msg && <small className="dg-msg" data-testid="alert-msg">{msg}</small>}
      <h3 className="overline" style={{ marginTop: 16 }}>Recent messages</h3>
      <div className="al-log" data-testid="notification-log">
        {!log.length && <small className="muted-label">Nothing sent yet.</small>}
        {log.map((x) => <p key={x.id}><em className={`pill ${x.status === "accepted" ? "success" : "mod-cancel"}`}>{x.status}{x.channel ? ` · ${x.channel}` : ""}</em> {x.kind} · {x.phone} <span>{fmtDT(x.at)}</span>{x.error && <small>{x.error}</small>}</p>)}
      </div>
    </div>
  );
}

export default function AlertsSetup() {
  return <><SmsPanel /><AlertsPanel /></>;
}
